import { useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import DownloadIcon from "@mui/icons-material/Download";
import "./imagePreview.scss";

const ImagePreview = ({
  title,
  imageUrl,
  altText = "Vista previa",
  thumbnailSize = 120, // Tamaño del thumbnail
  objectFit = "cover",
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = altText || "imagen";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Sin imagen ─────────────────────────────────────────────────────────────
  if (!imageUrl || error) {
    return (
      <div className="img-preview img-preview--empty">
        {title && <p className="img-preview__title">{title}</p>}
        <div 
          className="img-preview__placeholder"
          style={{ width: thumbnailSize, height: thumbnailSize }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="3" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <span>{error ? "Error" : "Sin imagen"}</span>
        </div>
      </div>
    );
  }

  // ── Con imagen ─────────────────────────────────────────────────────────────
  return (
    <>
      <div className="img-preview">
        {title && <p className="img-preview__title">{title}</p>}

        {/* Thumbnail clickeable */}
        <div 
          className="img-preview__thumbnail"
          style={{ width: thumbnailSize, height: thumbnailSize }}
          onClick={handleOpen}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleOpen()}
        >
          {/* Skeleton mientras carga */}
          {!loaded && (
            <div className="img-preview__skeleton">
              <div className="img-preview__spinner" />
            </div>
          )}

          <LazyLoadImage
            src={imageUrl}
            alt={altText}
            width={thumbnailSize}
            height={thumbnailSize}
            effect="blur"
            className={`img-preview__img ${loaded ? "img-preview__img--loaded" : ""}`}
            style={{ objectFit }}
            afterLoad={() => setLoaded(true)}
            onError={() => setError(true)}
          />

          {/* Overlay con icono de zoom */}
          <div className="img-preview__overlay">
            <ZoomInIcon className="img-preview__zoom-icon" />
          </div>
        </div>
      </div>

      {/* Modal con imagen grande */}
      <Modal
        open={modalOpen}
        onClose={handleClose}
        className="img-preview-modal"
        closeAfterTransition
      >
        <div className="img-preview-modal__container">
          {/* Header con controles */}
          <div className="img-preview-modal__header">
            <span className="img-preview-modal__title">{title || altText}</span>
            <div className="img-preview-modal__actions">
              <IconButton 
                onClick={handleDownload} 
                className="img-preview-modal__btn"
                title="Descargar"
              >
                <DownloadIcon />
              </IconButton>
              <IconButton 
                onClick={handleClose} 
                className="img-preview-modal__btn img-preview-modal__btn--close"
                title="Cerrar"
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          {/* Imagen grande */}
          <div className="img-preview-modal__content" onClick={handleClose}>
            <img
              src={imageUrl}
              alt={altText}
              className="img-preview-modal__image"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ImagePreview;